'use client';

import { CustomFormApi, Form, useFormState } from 'informed';
import { usePathname } from 'next/navigation';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { ResultWithSimilar } from './components/ResultWithSimilar';
import { AnalyticsEventName } from '@/api/analytics';
import {
  AnimatedItem,
  AnimatedWrapper,
} from '@/components/animation/AnimatedItem';
import { ButtonClose } from '@/components/buttons';
import { Input } from '@/components/form/Input';
import { BOTTOM_GAP } from '@/components/general/Header/HeaderMobile/ModalNav';
import { ModalSelectContainer } from '@/components/ModalSelect';
import { StaticModal } from '@/components/StaticModal';
import { useAnalytics } from '@/hooks/analytics';
import { useDebounce } from '@/hooks/debounce';
import { sendSearchOverallAppearanceEvent } from '@/modules/googleAnalytics/events/searchGamesEvents';
import { useCommonStore } from '@/store';

import { ScSearch, ScSearchForm, ScSearchModal } from './styled';
import { VerticalAlign } from '@/components/Modal/styled';

interface ISearchForm {
  search: string;
}

interface ISearchProps {
  onButtonClickAnalytics?: () => void;
}

export const Search: FC<ISearchProps> = ({ onButtonClickAnalytics }) => {
  const { t } = useTranslation('common');

  const { images } = useTheme();

  const { sendEventToAnalytics } = useAnalytics();

  const pathname = usePathname();

  const [isSearchOpened, setSearchOpened] = useState(false);

  const handleOpenSearch = useCallback(() => {
    onButtonClickAnalytics
      ? onButtonClickAnalytics()
      : sendEventToAnalytics({ name: AnalyticsEventName.GAME_SEARCH });

    setSearchOpened(true);
  }, [onButtonClickAnalytics, sendEventToAnalytics]);

  useEffect(() => {
    setSearchOpened(false);
  }, [pathname]);

  return (
    <ScSearch>
      <button className="button-search" onClick={handleOpenSearch}>
        <div className="value">{t('game.searchGame')}</div>
        <div className="icon">
          <images.searchGrayIcon.component />
        </div>
      </button>
      {isSearchOpened && (
        <SearchModalStatic
          onClose={async () => {
            await sendEventToAnalytics({
              name: AnalyticsEventName.MODAL__SEARCH__OVERALL__CLOSE,
            });
            setSearchOpened(false);
          }}
        />
      )}
    </ScSearch>
  );
};

interface ISearchModalProps {
  padding?: string;
  onClose?: () => void;
}

const SearchModal: FC<ISearchModalProps> = () => {
  const { t } = useTranslation('common');
  const { images } = useTheme();
  const { sendEventToAnalytics } = useAnalytics();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const formApiRef = useRef<CustomFormApi<ISearchForm>>();

  const [searchPhrase, setSearchPhrase] = useState('');
  const [isInputFocused, setIsInputFocused] = useState<boolean>(false);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    sendEventToAnalytics({
      name: AnalyticsEventName.MODAL__SEARCH__OVERALL__APPEARANCE,
    });
  }, [sendEventToAnalytics]);

  const handleChangeResult = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchPhrase(e.target.value);
  };

  const handleResetSearch = () => {
    if (formApiRef.current == null) return;

    setIsInputFocused(false);
    formApiRef.current.reset();
    setSearchPhrase('');
  };
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'instant',
      });
    }, 200);

    return () => clearTimeout(timeoutId);
  }, []);

  const searchTextDebounce = useDebounce(searchPhrase, 1000);

  return (
    <ScSearchModal>
      <ScSearchForm isFormFocused={isInputFocused}>
        <Form<ISearchForm> formApiRef={formApiRef} action="#">
          <Analytics />
          <Input
            autoComplete="off"
            name="search"
            type="search"
            placeholder={t('game.searchGame')}
            onChange={handleChangeResult}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                setIsInputFocused(false);
                (e.target as HTMLInputElement).blur();
              }
            }}
            onFocus={() => setIsInputFocused(true)}
            onBlur={() => {
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
              }
              timeoutRef.current = setTimeout(() => {
                setIsInputFocused(false);
              }, 100);
            }}
            autoFocus
          />
          {searchPhrase ? (
            <ButtonClose
              gray
              onClick={handleResetSearch}
              top={0}
              right={8}
              inCircle
            />
          ) : (
            <div className="search-icon">
              <images.searchIcon.component />
            </div>
          )}
        </Form>
      </ScSearchForm>
      <ResultWithSimilar searchPhrase={searchTextDebounce} />
    </ScSearchModal>
  );
};

export const SearchModalStatic: FC<
  Required<Pick<ISearchModalProps, 'onClose'>>
> = ({ onClose }) => {
  const { images } = useTheme();
  const { t } = useTranslation();

  useEffect(() => {
    sendSearchOverallAppearanceEvent();
  }, []);

  return (
    <StaticModal
      onClose={onClose}
      maxWidth={640}
      title={t('search')}
      icon={images.magnifier.component}
      verticalAlign={VerticalAlign.TOP}
      isPortal
    >
      <SearchModal />
    </StaticModal>
  );
};

export const SearchModalTabBar: FC<Required<ISearchModalProps>> = ({
  padding,
  onClose,
}) => {
  const { magnifierLargeIcon: MagnifierLargeIcon } = useTheme();
  const { t } = useTranslation();
  const tabBarHeight = useCommonStore.use.tabBarHeight();

  useEffect(() => {
    sendSearchOverallAppearanceEvent('tabbar');
  }, []);

  return (
    <ModalSelectContainer
      icon={<MagnifierLargeIcon />}
      title={t('search')}
      onClose={onClose}
      bottom={tabBarHeight - BOTTOM_GAP}
      padding={padding}
    >
      <AnimatedWrapper>
        <AnimatedItem>
          <SearchModal />
        </AnimatedItem>
      </AnimatedWrapper>
    </ModalSelectContainer>
  );
};

const Analytics = () => {
  const { sendEventToAnalytics } = useAnalytics();

  const { touched } = useFormState();
  const touchedForm = Object.values(touched).some((t) => t);

  useEffect(() => {
    if (touchedForm) {
      sendEventToAnalytics({
        name: AnalyticsEventName.MODAL__SEARCH__INPUT__TOUCHED,
      });
    }
  }, [touchedForm, sendEventToAnalytics]);

  return null;
};
